import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import Particles from "@/components/Particles";
import heroPhoto from "@/assets/da/portrait-2.inline";

const HERO_PHOTO = heroPhoto;

const Hero = () => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 50);
        return () => clearTimeout(t);
    }, []);

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative min-h-[100svh] w-full overflow-hidden grain bg-organic"
        >
            {/* Background image - face shifted RIGHT on mobile so it's visible next to text */}
            <div className="absolute inset-0">
                <img
                    src={HERO_PHOTO}
                    alt="Gilles Delieuze"
                    className="hero-portrait w-full h-full object-cover opacity-[0.6] md:opacity-[0.72]"
                    loading="eager"
                    data-testid="hero-photo"
                />
                {/* Mobile: stronger left fade to keep text readable, photo stays visible on the right */}
                <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/55 to-ink-900/0 md:via-ink-900/55 md:to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-b from-ink-900/30 via-transparent to-ink-900" />
            </div>

            {/* Halos - soft gold only */}
            <div className="halo bg-champagne/22 w-[520px] h-[520px] -top-20 -left-32" />
            <div className="halo bg-bronze/25 w-[420px] h-[420px] bottom-10 right-[-120px]" />

            <Particles count={22} />

            {/* Top nav */}
            <header className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 pt-7 md:pt-10 flex items-center justify-between">
                <Logo size="md" />
                <a
                    href="#sections"
                    data-testid="hero-scroll-link"
                    className="hidden md:flex items-center gap-2 text-[11px] text-ivory/55 hover:text-champagne transition-colors font-mono-tracked"
                >
                    Entrer dans le portail
                    <ChevronDown className="w-3.5 h-3.5" />
                </a>
            </header>

            {/* Vertical editorial tag */}
            <div className="hidden lg:flex absolute left-6 top-1/2 -translate-y-1/2 z-10">
                <span className="vertical-tag text-[10px] text-champagne/55 font-mono-tracked">
                    L'Agitateur de Particules · 27 ans de terrain
                </span>
            </div>

            {/* Main hero content */}
            <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 md:px-12 pt-16 sm:pt-20 md:pt-28 pb-24 md:pb-40">
                <div
                    className={`max-w-[58%] sm:max-w-[62%] md:max-w-2xl transition-all duration-[1400ms] ease-out ${
                        mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                    }`}
                >
                    <div className="flex items-center gap-3 mb-6">
                        <span className="h-px w-10 bg-champagne/60" />
                        <span className="text-[11px] font-mono-tracked text-champagne/85">
                            Portail · 2026
                        </span>
                    </div>

                    {/* H1 - editorial, smaller on mobile to leave room for the portrait next to it */}
                    <h1
                        translate="no"
                        className="notranslate font-display text-[12vw] leading-[0.94] sm:text-[11vw] md:text-[108px] lg:text-[124px] text-ivory-warm tracking-tight font-light"
                    >
                        <span className="block">Redevenir</span>
                        <span className="block italic font-light text-champagne-grad" style={{ fontStyle: 'italic' }}>
                            vivant
                        </span>
                    </h1>

                    <p className="mt-7 font-display italic text-lg sm:text-xl md:text-3xl text-ivory/85 max-w-xl leading-snug font-light">
                        n'est pas une théorie.
                    </p>

                    <p className="mt-6 text-sm sm:text-base md:text-lg text-ivory/70 max-w-md leading-relaxed">
                        Expérimentez. Ressentez. Transformez.
                    </p>

                    {/* Signature - elegant Cormorant italic gold, refined */}
                    <div className="mt-10 flex items-center gap-4">
                        <span
                            className="font-display italic text-3xl md:text-4xl text-champagne-grad"
                            style={{ fontWeight: 400, letterSpacing: "0.01em" }}
                        >
                            Gilles Delieuze
                        </span>
                        <span className="h-px w-12 bg-champagne/40" />
                    </div>

                    {/* CTAs */}
                    <div
                        className="mt-12 flex flex-col sm:flex-row flex-wrap gap-3"
                        data-testid="hero-cta-group"
                    >
                        <a
                            href="https://gillesdelieuze.com/lecole-des-maitres-dauphins/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-pill btn-pill-primary"
                            data-testid="cta-ecole"
                        >
                            <span aria-hidden>🐬</span>
                            <span>Découvrir l'École</span>
                        </a>
                        <a
                            href="https://t.me/+zLObzvUAXjVlYmY0"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-pill"
                            data-testid="cta-telegram"
                        >
                            <span aria-hidden>✨</span>
                            <span>Rejoindre Telegram</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-pulse-glow">
                <span className="text-[10px] font-mono-tracked text-champagne/70">scroll</span>
                <ChevronDown className="w-4 h-4 text-champagne/70" />
            </div>
        </section>
    );
};

export default Hero;
